import { createContext, useContext, useMemo } from 'react';
import useOptionsLookup, { getLookupCacheKey, LookupObject, UseOptionsLookupParams } from './hooks/useOptionsLookup';
import { LookupFieldObject } from './LookupFormField';

export interface LookupCacheEntry {
  options: any[] | null;
  loading: boolean;
  error: Error | null;
}

// Cache entries are keyed by the RESOLVED lookup key (base config + params as currently resolved
// against rootFormData — see getResolvedLookupKey), since that key changes when a lookup's params
// depend on form data that changes. `resolvedKeyByBase` lets a consumer (LookupFormField) look up
// its own lookup's CURRENT resolved key from the stable base key it can compute locally
// (getLookupCacheKey), without redoing any of the array's JNX param resolution itself.
export type ArrayLookupCache = Record<string, LookupCacheEntry>;

export interface ArrayLookupCacheContextValue {
  cache: ArrayLookupCache;
  resolvedKeyByBase: Record<string, string>;
}

const ArrayLookupCacheContext = createContext<ArrayLookupCacheContextValue | null>(null);



export function useArrayLookupCacheContext(
  lookup: LookupObject
){
  // Inside an array field, ArrayFieldTemplate owns fetching for every item property that has a
  // lookup: it resolves this lookup's params against the CURRENT rootFormData and shares one
  // fetch across every row, redoing it (once, still shared) whenever that resolution changes —
  // see getResolvedLookupKey. So whenever we're inside that context AND this lookup is one it
  // tracks (has an api/entity), we NEVER fetch here ourselves, even transiently while the array
  // catches up to a just-changed resolved key — that transient window is surfaced as `loading`.
  const arrayLookupCtx = useContext(ArrayLookupCacheContext);
  const {
      eligibleForArrayCache,
      cachedEntry
  } = useMemo<{
      eligibleForArrayCache: boolean,
      cachedEntry: any
  }>(() => {
    const baseKey = getLookupCacheKey(lookup);

    if (arrayLookupCtx && baseKey) {
      const resolvedKey = arrayLookupCtx.resolvedKeyByBase[baseKey!];
      const cachedEntry = resolvedKey ? arrayLookupCtx.cache[resolvedKey] : undefined;

      return {
        eligibleForArrayCache: true,
        cachedEntry
      }

    }

    return {
      eligibleForArrayCache: false,
      cachedEntry: undefined
    }
  }, [lookup, arrayLookupCtx]);

  return {
      eligibleForArrayCache,
      cachedEntry
  }
}


export function useOptionsLookupWithCache(params: UseOptionsLookupParams){
  const {eligibleForArrayCache, cachedEntry} = useArrayLookupCacheContext(params.lookup);

  // When cached, `precomputedOptions` (the shared, unfiltered fetch result) is still run through
  // this lookup's `filter` against the CURRENT rootFormData inside the hook — the array-level
  // pre-fetch only shares the expensive fetch, not a stale filtered snapshot from whenever that
  // fetch happened to run.
  const { options, loadingOptions: fetchedLoading, errorLoadingOptions: fetchedError } = useOptionsLookup({
    ...params,
    skip: eligibleForArrayCache,
    precomputedOptions: cachedEntry?.options,
  });

    const loadingOptions = eligibleForArrayCache ? (cachedEntry ? cachedEntry.loading : true) : fetchedLoading;
    const errorLoadingOptions = eligibleForArrayCache ? (cachedEntry ? cachedEntry.error : null) : fetchedError;

  return {
    eligibleForArrayCache,
    options, loadingOptions, errorLoadingOptions
  }
}


export default ArrayLookupCacheContext;
